import { observer } from "mobx-react-lite";
import { GraphData, prepareGraph } from "../utils/graph";
import React from "react";
import Chart from "react-google-charts";
import { Box, Skeleton, Stack, Text } from "@mantine/core";
import { yesterday } from "../utils/time";
import NoRecordsIcon from "../assets/icons/NoRecordsIcon";
import { RVUtils, isRenderedValue } from "../utils/renderedValue";

interface ElvGraphProps {
  data: GraphData | null;
  height: number;
  onColumnClick?: (column: string) => void;
  options?: Partial<GraphOptions>;
}

interface GraphOptions {
  // Select a minimum and maximum range for the graph.
  // If there is data outside this, it gets displayed anyways
  timeRange?: [Date, Date];
}

const ElvGraph: React.FC<ElvGraphProps> = observer(
  ({ data, height, onColumnClick, options }) => {
    if (data === null) {
      return <Skeleton height={height * 0.9} width="80%" mt={height * 0.1} />;
    } else if (data.length === 0) {
      const color = "#807B75";
      return (
        <Box
          h={height * 0.9}
          w="80%"
          mt={height * 0.1}
          style={{ justifyContent: "center", alignItems: "center" }}
          display="flex"
          bg="#F7F7F7"
        >
          <Stack align="center">
            <NoRecordsIcon color={color} />
            <Text c={color} size="sm">
              No data
            </Text>
          </Stack>
        </Box>
      );
    }

    const graphOptions = {
      curveType: "none",
      legend: { position: "bottom" },
      orientation: "horizontal",
      isStacked: true,
      colors: ["#BD6DFF", "#CB8BFF", "#E5C5FF", "#BDBDBD"],
      bar: {
        groupWidth: "80%",
      },
      hAxis: {
        gridlines: {
          // null doesn't work here
          color: "white",
        },
        ...(options?.timeRange && {
          minValue: options.timeRange[0],
          maxValue: options.timeRange[1],
        }),
      },
      chartArea: {
        width: "80%",
        height: "60%",
      },
      annotations: {
        alwaysOutside: true,
      },
      tooltip: {
        showColorCode: true,
      },
    };

    const graphProcessingOptions = {
      ...(options?.timeRange && {
        axisBounds: {
          min: options.timeRange[0],
          max: options.timeRange[1],
        },
      }),
    };

    const processedData = prepareGraph(data, graphProcessingOptions);

    const dataUsingLabels = [
      processedData[0]?.map((v) => {
        if (isRenderedValue(v)) {
          return RVUtils.label(v);
        } else {
          return v;
        }
      }),
      ...processedData.slice(1),
    ];

    return (
      <Box h={`${height}px`} w="100%">
        <Chart
          chartType="BarChart"
          width="100%"
          height={`${height}px`}
          data={dataUsingLabels}
          options={graphOptions}
          chartEvents={[
            {
              eventName: "select",
              callback: ({ chartWrapper }) => {
                if (onColumnClick === undefined) {
                  return;
                }
                const chart = chartWrapper.getChart();
                const selection = chart.getSelection();
                if (selection.length === 1) {
                  const [selectedItem] = selection;
                  const colIdx = selectedItem.column! as number;
                  const value = RVUtils.value(processedData[0]![colIdx]);
                  if (value === undefined) {
                    return;
                  }
                  onColumnClick(value);
                }
              },
            },
          ]}
        />
      </Box>
    );
  },
);

export default ElvGraph;
